// biome-ignore-all lint/suspicious/noExplicitAny: kintone field options are dynamic JSON structures
import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import type { FieldData, OptionItem } from "~/components/ui/itemModal/types";
import { normalizeMarkdownContent } from "~/features/projectTop/pageView/utils/markdownParser";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import type { RootState } from "~/store";
import { countFieldsForApp, MAX_FIELDS_PER_APP } from "~/utils/fieldDesignValidation";

interface AppData {
  app_name: string;
  properties: any[];
  app_id: string;
}

interface Property {
  label: string;
  required: {
    rows: [string, string, null, string][];
  };
  options?: any;
  id?: string;
}

const CHOICE_FIELD_TYPES = new Set(["DROP_DOWN", "RADIO_BUTTON", "CHECK_BOX", "MULTI_SELECT"]);

const kintoneOptionsDictToOptionItems = (options: unknown): OptionItem[] => {
  if (!options || typeof options !== "object" || Array.isArray(options)) return [];
  const entries = Object.entries(options as Record<string, any>).map(([k, v]) => {
    const label = String(v?.label ?? k);
    const indexRaw = v?.index ?? "0";
    const index = Number.parseInt(String(indexRaw), 10);
    return { label, index: Number.isFinite(index) ? index : 0 };
  });
  entries.sort((a, b) => a.index - b.index);
  return entries.map((e, idx) => ({ id: String(idx), value: e.label }));
};

const kintoneDefaultValueToDefaultSelectedOptions = (
  fieldType: string,
  defaultValue: unknown,
): string[] => {
  if (fieldType === "DROP_DOWN" || fieldType === "RADIO_BUTTON") {
    const v = typeof defaultValue === "string" ? defaultValue.trim() : "";
    return v ? [v] : [];
  }
  if (fieldType === "CHECK_BOX" || fieldType === "MULTI_SELECT") {
    if (Array.isArray(defaultValue)) {
      return defaultValue.map((v) => String(v).trim()).filter((v) => v);
    }
    const v = typeof defaultValue === "string" ? defaultValue.trim() : "";
    return v ? [v] : [];
  }
  return [];
};

const normalizeOptionsForUI = (fieldType: string, options: any): any => {
  if (!options || typeof options !== "object") return options;
  const out: any = { ...options };

  if (CHOICE_FIELD_TYPES.has(fieldType)) {
    if (!Array.isArray(out.options)) {
      out.options = kintoneOptionsDictToOptionItems(out.options);
    }
    if (!Array.isArray(out.defaultSelectedOptions)) {
      out.defaultSelectedOptions = kintoneDefaultValueToDefaultSelectedOptions(
        fieldType,
        out.defaultValue,
      );
    }
  }

  if (fieldType === "SUBTABLE" && out.fields && typeof out.fields === "object") {
    const newFields: Record<string, any> = {};
    Object.entries(out.fields as Record<string, any>).forEach(([k, child]) => {
      if (!child || typeof child !== "object") return;
      const childType = String((child as any).type ?? "").trim();
      const childOut: any = { ...child };

      const childOptionsValue = (childOut as any).options;
      const looksLikeUiContainer =
        childOptionsValue &&
        typeof childOptionsValue === "object" &&
        !Array.isArray(childOptionsValue) &&
        (Array.isArray((childOptionsValue as any).options) ||
          Object.hasOwn(childOptionsValue, "defaultSelectedOptions"));

      if (looksLikeUiContainer) {
        // already UI style
        newFields[k] = childOut;
        return;
      }

      // Convert kintone-style child (flat) into UI-style by nesting options.
      const uiOptions: any = {};
      const liftKeys = [
        "noLabel",
        "required",
        "unique",
        "maxLength",
        "minLength",
        "defaultValue",
        "minValue",
        "maxValue",
        "digit",
        "displayScale",
        "unit",
        "unitPosition",
        "protocol",
        "defaultNowValue",
        "expression",
        "hideExpression",
      ];
      liftKeys.forEach((key) => {
        if (key in childOut) {
          uiOptions[key] = childOut[key];
          delete childOut[key];
        }
      });

      if (CHOICE_FIELD_TYPES.has(childType)) {
        uiOptions.options = kintoneOptionsDictToOptionItems(childOut.options);
        uiOptions.defaultSelectedOptions = kintoneDefaultValueToDefaultSelectedOptions(
          childType,
          uiOptions.defaultValue,
        );
        delete uiOptions.defaultValue;
        delete childOut.options;
      }

      childOut.options = uiOptions;
      const key = String((childOut as any).code ?? "").trim() || k;
      newFields[key] = childOut;
    });
    out.fields = newFields;
  }

  return out;
};

export interface FieldDesignState {
  data: FieldData[];
  copyData: FieldData[];
  appData: AppData[];
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
  documentInfo: {
    createdAt: string;
    modifiedAt: string;
    modifiedBy?: {
      display_name: string;
    };
  } | null;
  isMarkdown: boolean;
  markdownContent: string;
  rawContent: string;
  initialMarkdownContent: string;
  sources: { type: string; id: string }[];
}

const initialState: FieldDesignState = {
  data: [],
  copyData: [],
  appData: [],
  isEditable: false,
  isLoading: false,
  error: null,
  documentInfo: null,
  isMarkdown: false,
  markdownContent: "",
  rawContent: "",
  initialMarkdownContent: "",
  sources: [],
};

// セレクターの定義
export const selectDocumentId = (state: RootState): string | undefined => {
  return state.auth.currentProject.outputs?.["field-design"]?.id;
};

export const fetchFieldDesign = createAsyncThunk(
  "fieldDesign/fetch",
  async (documentIdArg: string | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;

      // URLパラメータから取得したIDを優先、なければReduxストアから取得
      const documentId = documentIdArg || selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("フィールド設計書が見つかりません");
      }

      const documentApi = new DocumentApi();
      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        documentId,
      );
      if (response && "content" in response) {
        const rawContent = response.content ?? "";
        let parsedContent: any = null;
        let isMarkdown = false;
        let markdownContent = "";

        try {
          const jsonContent = JSON.parse(rawContent);
          if (Array.isArray(jsonContent?.apps)) {
            parsedContent = jsonContent;
          } else {
            isMarkdown = true;
            markdownContent = normalizeMarkdownContent(rawContent);
          }
        } catch (_error) {
          isMarkdown = true;
          markdownContent = normalizeMarkdownContent(rawContent);
        }

        return {
          data: parsedContent,
          documentInfo: {
            createdAt: response.created_at || "",
            modifiedAt: response.modified_at || "",
            modifiedBy: response.modified_by,
          },
          isMarkdown,
          markdownContent,
          rawContent,
          sources: response.sources ?? [],
        };
      }
      return rejectWithValue("データの取得に失敗しました");
    } catch (_error) {
      return rejectWithValue("データの取得中にエラーが発生しました");
    }
  },
);

export const acquireLock = createAsyncThunk(
  "fieldDesign/acquireLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;
      const documentId = selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("フィールド設計書が見つかりません");
      }

      const processLockApi = new ProcessLockApi();
      const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
        type: "field-design",
        mode: "edit",
      });
      if (response === null) {
        return rejectWithValue("編集権限を取得できませんでした");
      }
      return true;
    } catch (_error) {
      return rejectWithValue("編集権限の取得中にエラーが発生しました");
    }
  },
);

export const releaseLock = createAsyncThunk(
  "fieldDesign/releaseLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;
      const documentId = selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("フィールド設計書が見つかりません");
      }

      const processLockApi = new ProcessLockApi();
      const response = await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
        type: "field-design",
        mode: "edit",
      });
      if (response === null) {
        return rejectWithValue("編集権限の解放に失敗しました");
      }
      return true;
    } catch (_error) {
      return rejectWithValue("編集権限の解放中にエラーが発生しました");
    }
  },
);

export const updateFieldDesign = createAsyncThunk(
  "fieldDesign/update",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;
      const { data, appData, isMarkdown, markdownContent } = state.fieldDesign;
      const documentId = selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("フィールド設計書が見つかりません");
      }

      if (isMarkdown) {
        const documentApi = new DocumentApi();
        const response = await documentApi.updateContent(
          currentUser.organization_id,
          currentProject.id,
          documentId,
          { content: markdownContent },
        );
        if (response === null) {
          return rejectWithValue("データの更新に失敗しました");
        }

        await dispatch(releaseLock());
        return true;
      }

      const inferLookupBaseType = (field: FieldData): string => {
        const lookup = field.options?.lookup;
        if (!lookup?.relatedApp?.app || !lookup.relatedKeyField) {
          return "SINGLE_LINE_TEXT";
        }
        const relatedAppIdentifier = String(lookup.relatedApp.app).trim();
        const relatedApp = appData.find(
          (a) => a.app_id === relatedAppIdentifier || a.app_name === relatedAppIdentifier,
        );
        const relatedAppId = relatedApp?.app_id ?? relatedAppIdentifier;
        const relatedKey = String(lookup.relatedKeyField).trim();
        const relatedKeyField = data.find(
          (f) => f.app_id === relatedAppId && f.フィールドコード === relatedKey,
        );
        const relatedType = relatedKeyField?.フィールドタイプ ?? "";
        const numericTypes = new Set(["NUMBER", "RECORD_NUMBER"]);
        return numericTypes.has(relatedType) ? "NUMBER" : "SINGLE_LINE_TEXT";
      };

      const choiceTypes = new Set(["DROP_DOWN", "RADIO_BUTTON", "CHECK_BOX", "MULTI_SELECT"]);

      const optionItemsToKintoneOptions = (
        items: unknown,
      ): Record<string, { label: string; index: string }> => {
        if (!Array.isArray(items)) return {};
        const normalized = items
          .map((it: any) => String(it?.value ?? "").trim())
          .filter((v: string) => v.length > 0);
        const out: Record<string, { label: string; index: string }> = {};
        normalized.forEach((label, idx) => {
          out[label] = { label, index: String(idx) };
        });
        return out;
      };

      const normalizeOptionsForSave = (field: FieldData): any => {
        const options = field.options ?? {};
        if (!options || typeof options !== "object") return options;

        // Shallow clone to avoid mutating redux state.
        const out: any = { ...options };

        // Choice fields: convert UI OptionItem[] to kintone options dict + defaultValue.
        if (choiceTypes.has(field.フィールドタイプ)) {
          out.options = optionItemsToKintoneOptions(out.options);
          const defaults: string[] = Array.isArray(out.defaultSelectedOptions)
            ? out.defaultSelectedOptions.map((v: any) => String(v).trim()).filter((v: string) => v)
            : [];
          if (field.フィールドタイプ === "DROP_DOWN" || field.フィールドタイプ === "RADIO_BUTTON") {
            out.defaultValue = defaults[0] ?? "";
          } else {
            out.defaultValue = defaults;
          }
          delete out.defaultSelectedOptions;
        }

        // SUBTABLE children: UI keeps nested `child.options`; flatten to kintone-style child field properties.
        if (field.フィールドタイプ === "SUBTABLE" && out.fields && typeof out.fields === "object") {
          const newFields: Record<string, any> = {};
          Object.entries(out.fields as Record<string, any>).forEach(([k, child]) => {
            if (!child || typeof child !== "object") return;
            const childType = String((child as any).type ?? "").trim();
            const finalChild: any = { ...child };

            const childOptionsContainer = (child as any).options;
            const looksLikeUiContainer =
              childOptionsContainer &&
              typeof childOptionsContainer === "object" &&
              !Array.isArray(childOptionsContainer) &&
              (Array.isArray((childOptionsContainer as any).options) ||
                Object.hasOwn(childOptionsContainer, "defaultSelectedOptions") ||
                Object.hasOwn(childOptionsContainer, "required") ||
                Object.hasOwn(childOptionsContainer, "noLabel"));

            if (looksLikeUiContainer) {
              // Remove nested UI container and lift known options to top-level.
              delete finalChild.options;
              Object.entries(childOptionsContainer as Record<string, any>).forEach(([ok, ov]) => {
                if (ok === "options" || ok === "defaultSelectedOptions") return;
                finalChild[ok] = ov;
              });

              if (choiceTypes.has(childType)) {
                finalChild.options = optionItemsToKintoneOptions(
                  (childOptionsContainer as any).options,
                );
                const defaults: string[] = Array.isArray(
                  (childOptionsContainer as any).defaultSelectedOptions,
                )
                  ? (childOptionsContainer as any).defaultSelectedOptions
                      .map((v: any) => String(v).trim())
                      .filter((v: string) => v)
                  : [];
                if (childType === "DROP_DOWN" || childType === "RADIO_BUTTON") {
                  finalChild.defaultValue = defaults[0] ?? "";
                } else {
                  finalChild.defaultValue = defaults;
                }
              }
              delete finalChild.defaultSelectedOptions;
            } else if (choiceTypes.has(childType)) {
              // If the child is already kintone-style but options were somehow emitted as an array, normalize it.
              if (Array.isArray(finalChild.options)) {
                finalChild.options = optionItemsToKintoneOptions(finalChild.options);
              }
            }

            const key = String((finalChild as any).code ?? "").trim() || k;
            newFields[key] = finalChild;
          });
          out.fields = newFields;
        }

        return out;
      };

      // 現在の fieldTableData から API 形式の apps 配列を再構築
      const appsToSave = appData.map((app: AppData) => {
        // このアプリ ID に対応するフィールドデータを抽出
        const fieldsForThisApp = data.filter((field: FieldData) => field.app_id === app.app_id);

        // フィールドデータを properties の形式に変換
        const newProperties = fieldsForThisApp.map((field: FieldData) => {
          const rowType =
            field.フィールドタイプ === "LOOKUP" && field.options?.lookup
              ? inferLookupBaseType(field)
              : field.フィールドタイプ;
          // 元の properties の形式を再現
          const property: Property = {
            label: field.フィールド名, // フィールド名を label に
            required: {
              // required.rows の形式を再現
              rows: [
                [
                  field.app_id,
                  field.フィールドコード,
                  null, // 元の形式に合わせて null (または適切な値)
                  rowType,
                ],
              ],
            },
            // options をそのままコピー
            options: normalizeOptionsForSave(field),
          };
          return property;
        });

        // 元のアプリ情報に新しい properties を設定して返す
        return {
          ...app, // app_name, app_id などを維持
          properties: newProperties, // 再構築した properties を設定
        };
      });

      const documentApi = new DocumentApi();

      // 再構築したデータで payload を作成
      const updatePayload = {
        content: JSON.stringify({ apps: appsToSave }),
      };
      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        documentId, // id の取得方法を修正
        updatePayload, // 再構築したデータを使用
      );
      if (response === null) {
        return rejectWithValue("データの更新に失敗しました");
      }

      // データの更新が成功したら、ロックを解除
      await dispatch(releaseLock());

      return true;
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

export const resetData = createAsyncThunk("fieldDesign/resetData", async (_, { getState }) => {
  const state = getState() as RootState;
  const { copyData } = state.fieldDesign;
  return copyData;
});

const fieldDesignSlice = createSlice({
  name: "fieldDesign",
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<FieldData[]>) => {
      state.data = action.payload;
      state.isMarkdown = false;
      state.markdownContent = "";
      state.initialMarkdownContent = "";
    },
    setAppData: (state, action: PayloadAction<AppData[]>) => {
      state.appData = action.payload;
    },
    updateField: (
      state,
      action: PayloadAction<{
        fieldId: string;
        updates: Partial<FieldData>;
      }>,
    ) => {
      const { fieldId, updates } = action.payload;
      const fieldIndex = state.data.findIndex((field) => field["id"] === fieldId);
      if (fieldIndex !== -1) {
        const currentField = state.data[fieldIndex];
        if (currentField) {
          Object.assign(currentField, updates);
        }
      }
    },
    updateFieldCodeWithCascade: (
      state,
      action: PayloadAction<{
        fieldId: string;
        oldFieldCode: string;
        newFieldCode: string;
        appId: string;
      }>,
    ) => {
      const { fieldId, oldFieldCode, newFieldCode, appId } = action.payload;
      if (oldFieldCode === newFieldCode) return;

      // 1. フィールドコードを更新
      const fieldIndex = state.data.findIndex((f) => f["id"] === fieldId);
      if (fieldIndex === -1) return;
      const targetField = state.data[fieldIndex];
      if (targetField) {
        targetField.フィールドコード = newFieldCode;
      }

      // 2. 全フィールドのLOOKUP/REFERENCE_TABLE参照を連鎖更新
      // 対象アプリのapp_idまたはapp_nameで参照を特定
      const targetAppName = state.appData.find((a) => a.app_id === appId)?.app_name;
      const appIdentifiers = new Set([appId]);
      if (targetAppName) appIdentifiers.add(targetAppName);

      for (const field of state.data) {
        if (!field.options) continue;

        // LOOKUP の参照更新
        if (field.フィールドタイプ === "LOOKUP" && field.options.lookup) {
          const lookup = field.options.lookup;
          const relatedAppRef = String(lookup.relatedApp?.app ?? "").trim();

          // 参照先アプリのフィールドコード変更（relatedKeyField, relatedField, lookupPickerFields）
          if (appIdentifiers.has(relatedAppRef)) {
            if (lookup.relatedKeyField === oldFieldCode) {
              lookup.relatedKeyField = newFieldCode;
            }
            if (lookup.fieldMappings) {
              for (const mapping of lookup.fieldMappings) {
                if (mapping.relatedField === oldFieldCode) {
                  mapping.relatedField = newFieldCode;
                }
              }
            }
            if (lookup.lookupPickerFields) {
              lookup.lookupPickerFields = lookup.lookupPickerFields.map((code: string) =>
                code === oldFieldCode ? newFieldCode : code,
              );
            }
          }

          // 自アプリ側のフィールドコード変更（fieldMappings[].field）
          if (field.app_id === appId && lookup.fieldMappings) {
            for (const mapping of lookup.fieldMappings) {
              if (mapping.field === oldFieldCode) {
                mapping.field = newFieldCode;
              }
            }
          }
        }

        // REFERENCE_TABLE の参照更新
        if (field.フィールドタイプ === "REFERENCE_TABLE" && field.options.referenceTable) {
          const refTable = field.options.referenceTable;
          const relatedAppRef = String(refTable.relatedApp?.app ?? "").trim();

          // condition.relatedField は参照先アプリのフィールドコード
          if (appIdentifiers.has(relatedAppRef)) {
            if (refTable.condition?.relatedField === oldFieldCode) {
              refTable.condition.relatedField = newFieldCode;
            }
            if (refTable.displayFields) {
              refTable.displayFields = refTable.displayFields.map((code: string) =>
                code === oldFieldCode ? newFieldCode : code,
              );
            }
          }

          // condition.field は自アプリのフィールドコード
          if (field.app_id === appId && refTable.condition?.field === oldFieldCode) {
            refTable.condition.field = newFieldCode;
          }
        }
      }
    },
    addField: (state, action: PayloadAction<FieldData>) => {
      const newField = action.payload;
      const fieldsForApp = state.data.filter((f) => f.app_id === newField.app_id);
      const currentCount = countFieldsForApp(fieldsForApp);
      if (currentCount >= MAX_FIELDS_PER_APP) {
        // 上限に達している場合は追加を阻止
        return;
      }
      state.data.push(newField);
    },
    deleteField: (state, action: PayloadAction<string>) => {
      state.data = state.data.filter((field) => field["id"] !== action.payload);
    },
    reorderFields: (
      state,
      action: PayloadAction<{
        appId: string;
        newOrder: FieldData[];
      }>,
    ) => {
      const { appId, newOrder } = action.payload;
      const otherFields = state.data.filter((field) => field.app_id !== appId);
      state.data = [...otherFields, ...newOrder];
    },
    clearError: (state) => {
      state.error = null;
    },
    updateModifiedAt: (state) => {
      if (state.documentInfo) {
        state.documentInfo.modifiedAt = new Date().toISOString();
      }
    },
    updateMarkdownContent: (state, action: PayloadAction<string>) => {
      state.markdownContent = action.payload;
    },
    // テスト用: documentInfoを直接設定
    setDocumentInfo: (
      state,
      action: PayloadAction<{
        createdAt: string;
        modifiedAt: string;
        modifiedBy?: { display_name: string };
      } | null>,
    ) => {
      state.documentInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFieldDesign.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFieldDesign.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isMarkdown = action.payload.isMarkdown;
        state.markdownContent = action.payload.markdownContent;
        state.rawContent = action.payload.rawContent;
        state.initialMarkdownContent = action.payload.markdownContent;
        state.sources = action.payload.sources;
        if (action.payload.documentInfo.modifiedBy) {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
            modifiedBy: {
              display_name: action.payload.documentInfo.modifiedBy.display_name,
            },
          };
        } else {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
          };
        }

        if (state.isMarkdown) {
          state.appData = [];
          state.data = [];
          state.copyData = [];
        } else if (action.payload.data && Array.isArray(action.payload.data.apps)) {
          state.appData = action.payload.data.apps;
          // フィールドデータの生成
          const initialFieldData: FieldData[] = [];
          action.payload.data.apps.forEach((app: AppData) => {
            app.properties.forEach((prop: Property) => {
              if (prop.required?.rows && prop.required.rows.length > 0) {
                const row = prop.required.rows[0];
                if (row) {
                  const hasLookup =
                    typeof prop.options?.lookup === "object" && prop.options?.lookup !== null;
                  const originalFieldType = String(row[3] ?? "").trim();
                  const normalizedOptions = normalizeOptionsForUI(originalFieldType, prop.options);
                  initialFieldData.push({
                    id: prop.id || uuidv4(),
                    app_id: row[0],
                    フィールド名: prop.label, // フィールド名
                    フィールドコード: row[1], // フィールドコード
                    フィールドタイプ: hasLookup ? "LOOKUP" : originalFieldType, // フィールドタイプ
                    options: normalizedOptions,
                  });
                }
              }
            });
          });
          state.data = initialFieldData;
          state.copyData = JSON.parse(JSON.stringify(initialFieldData));
        } else {
          state.error =
            "フィールド設計書が前バージョンです。\nトップページから再生成してください。";
        }
      })
      .addCase(fetchFieldDesign.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(acquireLock.fulfilled, (state) => {
        state.isEditable = true;
        state.copyData = JSON.parse(JSON.stringify(state.data));
        state.initialMarkdownContent = state.markdownContent;
      })
      .addCase(acquireLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(releaseLock.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(releaseLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateFieldDesign.fulfilled, (state) => {
        state.isEditable = false;
        if (state.isMarkdown) {
          state.initialMarkdownContent = state.markdownContent;
        }
      })
      .addCase(updateFieldDesign.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(resetData.fulfilled, (state, action) => {
        if (state.isMarkdown) {
          state.markdownContent = state.initialMarkdownContent;
        } else {
          state.data = action.payload;
        }
      });
  },
});

export const {
  setData,
  setAppData,
  updateField,
  updateFieldCodeWithCascade,
  addField,
  deleteField,
  reorderFields,
  clearError,
  updateModifiedAt,
  updateMarkdownContent,
  setDocumentInfo,
} = fieldDesignSlice.actions;

export default fieldDesignSlice.reducer;
